'use client';

import Image from 'next/image';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { lightLogoFrameBoxStyle } from '@/lib/constants/logo-frame';
import { ProviderLogoPlaceholder } from './ProviderLogoPlaceholder';

/** Border + background for light/inverted logos (readable on dark surfaces). */
export { LIGHT_LOGO_FRAME_TW as PROVIDER_LIGHT_LOGO_FRAME_CLASS } from '@/lib/constants/logo-frame';

interface ProviderLogoProps {
  logoUrl?: string | null;
  companyName: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** When true, use a dark frame so light artwork remains visible (matches admin). */
  isLight?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
  xl: 'w-24 h-24',
};

const sizePixels = {
  sm: 32,
  md: 48,
  lg: 64,
  xl: 96,
};

export const ProviderLogo = ({
  logoUrl,
  companyName,
  size = 'md',
  isLight = false,
  className,
}: ProviderLogoProps) => {
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);

  // Preserve the table layout when a stored logo is missing or inaccessible.
  if (!logoUrl || failedLogoUrl === logoUrl) {
    return (
      <ProviderLogoPlaceholder
        companyName={companyName}
        size={size}
        className={className}
      />
    );
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg flex-shrink-0',
        sizeClasses[size],
        !isLight && 'border border-gray-200 bg-white',
        className
      )}
      style={isLight ? lightLogoFrameBoxStyle : undefined}
    >
      <Image
        src={logoUrl}
        alt={`${companyName} logo`}
        width={sizePixels[size]}
        height={sizePixels[size]}
        className="object-contain w-full h-full"
        unoptimized={logoUrl.endsWith('.svg')}
        onError={() => {
          setFailedLogoUrl(logoUrl);
        }}
      />
    </div>
  );
};
